import React from 'react'
import { useOne, useUpdate, useGetIdentity, useInvalidate } from '@refinedev/core'
import { App, Button, Card, Skeleton, Space, Typography } from 'antd'
import { Link } from 'react-router'
import { brand } from '../../theme/brand'
import { Pill, type Tone } from '../../theme/listkit'
import { kindSpec, resolutionNote, capitalize, type ModeratedKind } from '../businesses/contentModeration'
import { useContentModeration } from '../businesses/useContentModeration'
import { headStyle, Field, fmtDate } from './reportUiKit'
import type { Report, Identity } from './show'
import { ReportedMemoryComment } from './ReportedMemoryComment'

interface ModeratedAuthor {
  name: string | null
  class_year: string | null
}

interface ModeratedBusiness {
  id: string
  name: string
}

interface ModeratedRow {
  id: string
  status: string
  created_at: string
  sentence?: string
  body?: string
  is_private?: boolean
  removed_reason?: string | null
  author?: ModeratedAuthor | null
  business?: ModeratedBusiness | null
}

function statusTone(kind: ModeratedKind, status: string): Tone {
  if (status === 'removed') return 'red'
  if (kind === 'business_request' && status === 'closed') return 'neutral'
  return 'green'
}

function statusLabel(kind: ModeratedKind, status: string): string {
  if (kind === 'business_request') {
    if (status === 'open') return 'Open'
    if (status === 'closed') return 'Closed'
    if (status === 'removed') return 'Removed'
    return status
  }
  return status === 'removed' ? 'Removed' : 'Visible'
}

/**
 * Reported-content card on the report show page (Task P3), rendered between
 * "Report details" and "Triage actions" in show.tsx. Lets an admin remove or
 * restore the reported vouch, owner note or Ask reply, or remove an Ask
 * request with a reason — all without leaving the report.
 *
 * A `memory_comment` report hands off to ReportedMemoryComment (kindSpec has
 * no entry for it, so that catalog stays Jaguar-Owned-only). Every other
 * non-Jaguar kind still renders nothing (kindSpec returns null for
 * post/photo/business/avatar/profile/job/message) — those keep their
 * existing report flow, untouched by this card.
 */
export function ReportedContent({ report }: { report: Report | undefined }) {
  const spec = report ? kindSpec(report.target_type) : null
  // Hooks below must run on every render regardless of whether this report is
  // Jaguar-Owned content — the `if (!spec) return null` happens only AFTER
  // every hook call, so hook order never changes across renders.
  const kind = (spec ? report!.target_type : 'business_vouch') as ModeratedKind
  const { remove, restore, isPending } = useContentModeration(kind)
  const { query, result } = useOne<ModeratedRow>({
    resource: spec?.resource ?? 'business_vouches',
    id: report?.target_id,
    meta: { select: spec?.select ?? '' },
    queryOptions: { enabled: !!spec && !!report?.target_id },
  })
  const { mutate: updateReport } = useUpdate()
  const { data: identity } = useGetIdentity<Identity>()
  const invalidate = useInvalidate()
  const { message } = App.useApp()

  if (report?.target_type === 'memory_comment') return <ReportedMemoryComment report={report} />

  if (!spec || !report) return null

  // Fix round 1, item 2: a failed report stamp used to fail silently. The
  // content removal itself already succeeded and already toasted
  // (useContentModeration's own message.success) — this is a SEPARATE
  // failure, on the report's own status, and needs its own visible error so
  // an admin isn't left thinking the report was actioned when it wasn't.
  function stampReportActioned(reason: string | undefined) {
    if (!report || !spec) return
    if (report.status === 'actioned' || report.status === 'dismissed') return
    // Captured into a plain local so the nested onError closure below doesn't
    // need to re-narrow `spec` itself — TS doesn't carry a `const`'s
    // null-check narrowing across a function boundary.
    const noun = spec.noun
    updateReport(
      {
        resource: 'reports',
        id: report.id,
        values: {
          status: 'actioned',
          resolution_note: resolutionNote(kind, reason),
          reviewed_by: identity?.id ?? null,
          reviewed_at: new Date().toISOString(),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          invalidate({ resource: 'reports', invalidates: ['detail'], id: report.id })
        },
        onError: (e) => {
          message.error(
            `Removed the ${noun}, but the report couldn't be marked actioned: ` +
              ((e as { message?: string } | undefined)?.message ?? 'unknown error'),
          )
        },
      },
    )
  }

  async function handleRemove() {
    const id = result?.id
    if (!id) return
    // Fix round 1, item 3: the reason for a request comes from the hook's
    // own onRemoved callback — the EXACT trimmed value it just wrote — not
    // from refetching the row afterward. A refetch that itself fails would
    // otherwise silently stamp the report with no reason at all.
    //
    // Task P8 (final review, "ReportedContent refreshes twice"): this used
    // to call `query.refetch()` itself right here — a SECOND fetch of the
    // exact same detail row `useUpdate` had already invalidated one line
    // earlier, inside useContentModeration's own `mutate()` call. Refine's
    // default `invalidates: ['detail']` already refreshes this card's
    // `useOne` (same resource + id), so the card's display stays current
    // with no explicit call here — and, same as before, that background
    // refresh's success or failure still never gates the report stamp.
    await remove(id, { onRemoved: (reason) => stampReportActioned(reason) })
  }

  async function handleRestore() {
    const id = result?.id
    if (!id) return
    await restore(id)
  }

  // Fix round 1, item 8: check for missing row data BEFORE isError — a
  // refetch that fails after a successful remove/restore must not replace
  // perfectly good (if now slightly stale) content with "no longer exists".
  // isError alone used to trigger that copy even when `result` still held
  // the last-known-good row.
  if (!result || !result.id) {
    if (query.isLoading) {
      return (
        <Card title={<span style={headStyle}>Reported content</span>} style={{ marginBottom: 16, maxWidth: 720 }}>
          <Skeleton active />
        </Card>
      )
    }
    return (
      <Card title={<span style={headStyle}>Reported content</span>} style={{ marginBottom: 16, maxWidth: 720 }}>
        <Typography.Text style={{ fontFamily: brand.fSans, fontSize: 13, color: brand.sub, fontStyle: 'italic' }}>
          This content no longer exists. Its author may have deleted it.
        </Typography.Text>
      </Card>
    )
  }

  const row = result
  const text = row.sentence ?? row.body ?? ''
  const isRemoved = row.status === 'removed'
  const isPrivateRequest = kind === 'business_request' && !!row.is_private

  return (
    <Card title={<span style={headStyle}>Reported content</span>} style={{ marginBottom: 16, maxWidth: 720 }}>
      <Field label="Author">
        <Space size={8} align="center">
          <span>
            {row.author?.name ?? 'Unknown member'}
            {row.author?.class_year ? ` · Class of ${row.author.class_year}` : ''}
          </span>
          {isPrivateRequest ? <Pill tone="jaguar">Private request</Pill> : null}
        </Space>
      </Field>
      {row.business ? (
        <Field label="Business">
          <Link to={`/businesses/show/${row.business.id}`}>{row.business.name}</Link>
        </Field>
      ) : null}
      <Field label="Posted">{fmtDate(row.created_at)}</Field>
      <Field label="Status">
        <Pill tone={statusTone(kind, row.status)}>{statusLabel(kind, row.status)}</Pill>
      </Field>
      <Field label={capitalize(spec.noun)}>
        <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: brand.fSans }}>
          {text}
        </Typography.Paragraph>
      </Field>
      {kind === 'business_request' && row.status === 'removed' && row.removed_reason ? (
        <Field label="Removed because">{row.removed_reason}</Field>
      ) : null}

      <Space wrap>
        {!isRemoved ? (
          <Button danger loading={isPending} data-testid="reported-content-remove" onClick={() => void handleRemove()}>
            {`Remove ${spec.noun}`}
          </Button>
        ) : null}
        {isRemoved && spec.canRestore ? (
          <Button loading={isPending} data-testid="reported-content-restore" onClick={() => void handleRestore()}>
            {`Restore ${spec.noun}`}
          </Button>
        ) : null}
        {isRemoved && !spec.canRestore ? (
          <Typography.Text style={{ fontFamily: brand.fSans, fontSize: 12.5, color: brand.sub, fontStyle: 'italic' }}>
            A removed request can&apos;t be restored.
          </Typography.Text>
        ) : null}
      </Space>
    </Card>
  )
}
